import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  params, string, arrayOf, number, bool,
  shape, useDataModel, extend
} from '@thd-nucleus/data-sources';
import PropTypes from 'prop-types';
import {
  ProductPod,
  ProductIdentifier,
  ProductATC,
  ProductRating,
  ProductImage
} from '@thd-olt-component-react/product-pod';
import { Button } from '@one-thd/sui-atomic-components';
import { ProductQuantity } from '@thd-olt-component-react/quantity';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { AddToContext } from '../../AddToContext';
import { SCREENS, TRACK } from '../../AddToConstants';

export const AddToProductPod = ({
  itemId,
  index,
  updateList,
  basePrice,
  quantity: quantityProp,
  isIntent = false,
  handleResolveIntent
}) => {
  const [productSelection, setProductSelection] = useState({ itemId: null, quantity: null });
  const [qty, setQty] = useState(quantityProp ?? 1);
  const [nextScreenName, setNextScreenName] = useState(null);
  const {
    setScreen, setSelectedProductDetails, products, addToType,
    setShowSuccessMessage, setShowErrorMessage, setSourceScreen, storeId
  } = useContext(AddToContext);
  const superSkuChangedItemIdsRef = useRef([]);

  const analyticsData = {
    position: index,
    parent: 'add-to-search-results-mini-plp'
  };

  const {
    LIST_SELECT_GROUP,
    MINI_PIP_BASE,
    DEFAULT
  } = SCREENS;

  const { data: productData, error, loading } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId
  });

  const onQtyUpdate = ({ quantity }) => {
    setQty(quantity);
  };

  const handleSelectedProductClick = async (item, itemQuantity, screenName) => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setProductSelection({ itemId: item, quantity: itemQuantity });
    if (addToType === 'Project' && screenName !== MINI_PIP_BASE) {
      if (isIntent) {
        await handleResolveIntent({
          omsId: productData?.product?.identifiers?.itemId,
          sku: productData?.product?.identifiers?.storeSkuNumber
        });
        return;
      }
      setNextScreenName(DEFAULT);
    } else if (addToType === 'List' && screenName !== MINI_PIP_BASE) {

      if (isIntent && handleResolveIntent) {
        handleResolveIntent({ itemId: item, quantity: itemQuantity });
      } else if (!products || products.length === 0) {
        // Redirect to group selection screen for list items when no products passed
        setSourceScreen(SCREENS.MINI_PLP);
        setNextScreenName(LIST_SELECT_GROUP);
      } else {
        // Original behavior when products exist
        updateList({
          variables: {
            products: [{ itemId: item, quantity: itemQuantity }]
          }
        });
        window.LIFE_CYCLE_EVENT_BUS.trigger(TRACK.ADD_TO_LIST_ADD_ACTION, {
          name: 'list add',
          location: 'lists: mini plp',
          items: [{
            quantity: itemQuantity,
            // Does this need to be basePrice * quantity?
            price: basePrice || 'N/A',
            sku: item || 'N/A',
            aiQuantityEstimate: 'no'
          }],
          isBulkAction: false
        });
      }
    } else {
      setNextScreenName(screenName);
    }
  };

  useEffect(() => {
    if (productData?.product?.itemId && nextScreenName && productSelection) {
      setSelectedProductDetails({ itemId: productSelection.itemId, quantity: productSelection.quantity, productData });
      setScreen(nextScreenName);
    }
  }, [productData, productSelection]);

  return (
    <ProductPod
      itemId={itemId}
      analyticsData={analyticsData}
      render={(pod) => {
        return (
          <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="sui-cursor-pointer"
              onClick={() => handleSelectedProductClick(pod.itemId, qty, MINI_PIP_BASE)}
              data-testid="mini-pip-nav-container"
            >
              <div className="sui-pointer-events-none">
                <div className="sui-w-full sui-flex sui-gap-4">
                  <div className="sui-w-36 sui-min-w-36 sui-h-auto sui-mt-1">
                    <ProductImage itemId={pod.itemId} target="_blank" />
                  </div>
                  <div className="sui-gap-1">
                    <div className="sui-flex">
                      <ProductHeader
                        brand="inline"
                        itemId={pod.itemId}
                        titleMaxLine={3}
                        disableShopThisCollection
                        target="_blank"
                      />
                    </div>
                    <ProductRating itemId={pod.itemId} target="_blank" />
                    <ProductIdentifier itemId={pod.itemId} type="itemId" />
                    <ProductIdentifier itemId={pod.itemId} type="storeSku" />
                  </div>
                </div>
              </div>
            </div>
            <div className="sui-w-full sui-flex sui-gap-4">
              <div className="sui-flex sui-items-center sui-w-1/2 sui-h-[44px]">
                <ProductQuantity
                  itemId={pod.itemId}
                  onQuantityChange={onQtyUpdate}
                  quantity={qty}
                />
              </div>
              <div className="sui-w-1/2 sui-pt-1">
                <SuperSkuMiniSwatches
                  itemId={pod.itemId}
                  storeId={storeId}
                  onChange={(clickData) => {
                    if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
                      superSkuChangedItemIdsRef.current = [
                        ...superSkuChangedItemIdsRef.current,
                        clickData.itemId,
                      ];
                    }
                    pod.onChange(clickData);
                  }}
                  onHover={pod.onSSKUHover}
                  numberOfMiniSwatches={4}
                  hideComponentWhenEmpty
                  removeStaticHeight
                />
              </div>
            </div>
            <div className="sui-w-full sui-flex sui-gap-4 sui-items-start">
              <div className="sui-pointer-events-none sui-flex-1 sui-min-w-0">
                <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} hideForChooseYourOptions />
                <FulfillmentPodShipping itemId={pod.itemId} storeId={storeId} />
              </div>
              <div className="sui-flex-shrink-0 sui-pt-1 sui-min-w-[120px]">
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  showPreferredPricingBadge
                  hideForProductTypes={['HDQC']}
                  stackWasPrice
                />
              </div>
            </div>
            <Button
              fullWidth
              onClick={() => handleSelectedProductClick(pod.itemId, qty)}
            >
              Add to {addToType}
            </Button>
          </>
        );
      }}
    />
  );
};

AddToProductPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  basePrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  updateList: PropTypes.func,
  quantity: PropTypes.number,
  isIntent: PropTypes.bool,
  handleResolveIntent: PropTypes.func
};

AddToProductPod.defaultProps = {
  basePrice: null,
  updateList: () => { },
  quantity: null,
  isIntent: false,
  handleResolveIntent: () => { }
};

AddToProductPod.dataModel = extend(
  {
    product: params({
      itemId: string()
        .isRequired(),
      dataSource: string()
    }).shape({
      itemId: string(),
      bundleFlag: bool(),
      bundleItems: arrayOf(
        shape({
          id: string(),
          quantity: number(),
        })
      ),
      dataSources: string(),
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        itemId: string(),
        storeSkuNumber: string(),
        omsThdSku: string(),
        specialOrderSku: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      })
    })
  },
  ProductPod,
  ProductIdentifier,
  ProductImage,
  ProductHeader,
  ProductRating,
  Price,
  ProductQuantity,
  ProductATC,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  SuperSkuMiniSwatches
);
